import React, { useEffect, useState } from 'react';

import { Alert, Button, Group, LoadingOverlay, Stack, Text, px, useMantineTheme } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import useAthlete from 'hooks/athlete/useAthlete';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import useDeletePaymentMethod from 'hooks/paymentMethod/useDeletePaymentMethod';
import usePaymentMethod from 'hooks/paymentMethod/usePaymentMethod';
import { getPaymentMethodName } from 'utils/formatters';
import notificationHandler from 'utils/notifications';

interface IDeletePaymentMethodModalProps {
  paymentMethodId: string;
  handleClose: () => void;
  isOwnerView?: boolean;
  isPrimaryAcc?: boolean;
}

const DeletePaymentMethodModal = ({
  paymentMethodId,
  handleClose,
  isOwnerView,
  isPrimaryAcc,
}: IDeletePaymentMethodModalProps): JSX.Element => {
  const theme = useMantineTheme();
  const { trackPaymentMethodDeleteClicked } = useAthleteTracking();

  const { data: paymentMethodData, isLoading, isSuccess, isError } = usePaymentMethod(paymentMethodId);
  const { data: athleteData } = useAthlete(paymentMethodData?.data.athlete);
  const deletePaymentMethodMutation = useDeletePaymentMethod();
  const [paymentMethodTitle, setPaymentMethodTitle] = useState<string>();

  useEffect(() => {
    if (isSuccess && paymentMethodData) {
      const name = getPaymentMethodName(paymentMethodData.data);
      setPaymentMethodTitle(name);
    }
  }, [paymentMethodData, isSuccess]);

  const handleDelete = (): void => {
    trackPaymentMethodDeleteClicked(paymentMethodData?.data.failureCode);
    const { successNotification, errorNotification } = notificationHandler();
    deletePaymentMethodMutation.mutate(paymentMethodId, {
      onSuccess: () => {
        successNotification('Payment Method delete success', `${paymentMethodTitle} successfully deleted.`);
        handleClose();
      },
      onError: error => {
        errorNotification('Payment Method delete failed', error.response?.data.message);
      },
    });
  };

  return (
    <Stack>
      <LoadingOverlay visible={isLoading} />
      {isSuccess && (
        <>
          <Text>
            {`Are you sure you want to delete ${paymentMethodTitle}?` +
              (isPrimaryAcc
                ? ' Removing a payment method to this profile will remove this payment method for ALL linked accounts.'
                : '')}
          </Text>
          {isOwnerView && (
            <Text>
              {`This payment method is currently attached to ${athleteData?.data.firstName} ${athleteData?.data.lastName}.`}
            </Text>
          )}
          <Text color='dimmed'>This action cannot be undone.</Text>
          <Group position='right'>
            <Button onClick={handleDelete} color='red' loading={deletePaymentMethodMutation.isLoading}>
              Delete
            </Button>
          </Group>
        </>
      )}
      {isError && (
        <Alert icon={<IconExclamationCircle size={px(theme.fontSizes.md)} />} title='Error' color='red'>
          Something went wrong while fetching data!
        </Alert>
      )}
    </Stack>
  );
};

export default DeletePaymentMethodModal;
